import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { ClockEntryWithTask } from 'src/models/timeclock/entry/_types';
import { User } from 'src/models/user/_types';
import { isHeadTrainer, isSubTrainer } from 'src/models/user/utils';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { useTasks } from './useTasks';
import { useTimeClock } from './useTimeClock';

interface Props {
  onSubmit: () => void;
  user: User;
  clockedIn: boolean;
  logEntry: ClockEntryWithTask | undefined | null;
}

const TimeClock = ({ onSubmit, user, clockedIn, logEntry }: Props) => {
  const handleClose = () => {
    onSubmit();
  };

  const { tasks, tasksLoading } = useTasks();
  const { trainer } = useSession();

  const { formikClockIn, formikClockOut } = useTimeClock({
    user: user,
    onSuccessCallback: handleClose,
    notes: (clockedIn ? logEntry?.notes : '') || '',
  });

  const formik = clockedIn ? formikClockOut : formikClockIn;

  const { t } = useTranslation('translation', { keyPrefix: 'timetracking' });

  const buttonText = clockedIn ? t('Clock Out') : t('Clock In');
  if (tasksLoading || !tasks) {
    return null;
  }
  const taskName = logEntry?.task?.name || '';
  const userNameText = clockedIn ? 'Clocking out' : 'Clocking in';

  // Filter tasks based on user permissions
  const allowedTasks = tasks.filter((task) => {
    // If user is a head trainer, they can see all tasks
    if (isHeadTrainer(user)) {
      return true;
    }

    // If user is a sub-trainer, filter based on disabledTasks
    if (isSubTrainer(user) && trainer) {
      const disabledTaskIds = new Set(trainer.disabledTasks || []);
      return !disabledTaskIds.has(task.id.toString());
    }

    // For regular users, show all tasks
    return true;
  });

  const orderedTasks = allowedTasks.sort((a, b) => {
    return a.sortOrder - b.sortOrder;
  });

  return (
    <Formik {...formik}>
      {({ handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <Stack spacing={3} onKeyDown={(event) => event.stopPropagation()}>
            <Typography variant="body1">
              {t(userNameText, {
                value: user.firstName + ' ' + user.lastName,
              })}
            </Typography>
            {clockedIn ? (
              <Stack spacing={0} sx={{ mr: 1 }}>
                <Typography variant="body1">
                  {t('Clocked in for task')} {taskName}
                </Typography>
              </Stack>
            ) : (
              <FormTextField
                label={t('Task')}
                name="taskId"
                fullWidth
                required
                options={orderedTasks.map((option) => ({
                  label: option.name,
                  value: option.id,
                }))}
              />
            )}
            <FormTextField
              multiline
              label={t('Notes')}
              name="notes"
              minRows={3}
            />
            <Button
              disabled={isSubmitting}
              type="submit"
              variant="prominent"
              fullWidth
            >
              {buttonText}
            </Button>
          </Stack>
        </form>
      )}
    </Formik>
  );
};

export default TimeClock;
